import { Form, Link } from '@remix-run/react'
import * as React from 'react'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { WEDDING } from '#app/config/wedding.ts'
import { Icon } from '#app/components/ui/icon.tsx'
import { controlClass } from '#app/components/ui/input.tsx'
import { cn } from '#app/utils/misc.tsx'

/* ───────────────────────── photo-panel captions ───────────────────────── */

export function QuoteCaption({ quote, cite }: { quote: React.ReactNode; cite: React.ReactNode }) {
	return (
		<>
			<p className="max-w-[440px] font-serif text-[28px] italic leading-[1.25]">{quote}</p>
			<p className="mono mt-3 text-white/75">{cite}</p>
		</>
	)
}

export function NamesCaption({ eyebrow, children }: { eyebrow: React.ReactNode; children: React.ReactNode }) {
	return (
		<>
			<p className="mono text-white/75">{eyebrow}</p>
			<p className="mt-3 font-serif text-[64px] leading-[.9] tracking-[-.01em] [&_em]:italic [&_em]:text-brown-light">
				{children}
			</p>
		</>
	)
}

export function AuthFooter({ left, right }: { left?: React.ReactNode; right?: React.ReactNode }) {
	return (
		<>
			<span>{left ?? <span className="mono">{WEDDING.hashtag}</span>}</span>
			<span>
				{right ?? (
					<>
						<Link to="/privacy" className="hover:text-ink">
							Privacy
						</Link>{' '}
						·{' '}
						<Link to="/tos" className="hover:text-ink">
							Terms
						</Link>
					</>
				)}
			</span>
		</>
	)
}

export function AuthAlt({ children }: { children: React.ReactNode }) {
	return (
		<p className="mt-7 border-t border-line pt-6 text-sm text-stone [&_a]:border-b [&_a]:border-current [&_a]:pb-0.5 [&_a]:text-ink hover:[&_a]:text-brown">
			{children}
		</p>
	)
}

/* ───────────────────────── inputs ───────────────────────── */

/** Password input with a show/hide toggle (prototype `.pw-wrap`). */
export const PasswordInput = React.forwardRef<
	HTMLInputElement,
	React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
	const [show, setShow] = React.useState(false)
	return (
		<div className="relative">
			<input
				ref={ref}
				{...props}
				type={show ? 'text' : 'password'}
				className={cn(controlClass, 'h-12 px-4 pr-12', className)}
			/>
			<button
				type="button"
				onClick={() => setShow(s => !s)}
				aria-label={show ? 'Hide password' : 'Show password'}
				className={cn(
					'absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full transition-colors hover:bg-paper-2',
					show ? 'text-ink' : 'text-stone',
				)}
			>
				<Icon name={show ? 'eye-slash' : 'eye'} className="h-[18px] w-[18px]" />
			</button>
		</div>
	)
})
PasswordInput.displayName = 'PasswordInput'

/** 0–4 strength bars (prototype `.pw-meter`). */
export function PasswordMeter({ value }: { value: string }) {
	let s = 0
	if (value) {
		if (value.length >= 8) s++
		if (value.length >= 12) s++
		if (/[A-Z]/.test(value) && /[a-z]/.test(value)) s++
		if (/\d/.test(value) && /[^A-Za-z0-9]/.test(value)) s++
		s = Math.max(1, Math.min(4, s))
	}
	const tone = ['bg-line', 'bg-bad', 'bg-warn-dot', 'bg-ok', 'bg-ok'][s]
	return (
		<div aria-hidden className="mt-1 flex gap-1">
			{[1, 2, 3, 4].map(i => (
				<span key={i} className={cn('h-[3px] flex-1 rounded-sm transition-colors', i <= s ? tone : 'bg-line')} />
			))}
		</div>
	)
}

/** Native select styled like the prototype `.select`. */
export const NativeSelect = React.forwardRef<
	HTMLSelectElement,
	React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, ...props }, ref) => (
	<select
		ref={ref}
		{...props}
		className={cn(
			controlClass,
			'h-12 appearance-none bg-[length:12px] bg-[right_16px_center] bg-no-repeat px-4 pr-10',
			"bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236F685C' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")]",
			className,
		)}
	>
		{children}
	</select>
))
NativeSelect.displayName = 'NativeSelect'

/**
 * Alphanumeric one-time code boxes (email codes use A–Z + 1–9). Joined into a hidden
 * input `name`. Paste fills every box.
 */
export function CodeBoxes({
	name,
	length = 6,
	defaultValue = '',
	invalid,
	autoFocus,
}: {
	name: string
	length?: number
	defaultValue?: string
	invalid?: boolean
	autoFocus?: boolean
}) {
	const clean = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '')
	const [chars, setChars] = React.useState<string[]>(() =>
		Array.from({ length }, (_, i) => clean(defaultValue)[i] ?? ''),
	)
	const refs = React.useRef<Array<HTMLInputElement | null>>([])
	return (
		<div className="flex gap-2.5">
			<input type="hidden" name={name} value={chars.join('')} />
			{chars.map((c, i) => (
				<input
					key={i}
					ref={el => {
						refs.current[i] = el
					}}
					aria-label={`Character ${i + 1}`}
					autoComplete={i === 0 ? 'one-time-code' : 'off'}
					autoCapitalize="characters"
					autoFocus={autoFocus && i === 0}
					maxLength={1}
					aria-invalid={invalid || undefined}
					value={c}
					className="h-[60px] w-full min-w-0 max-w-[56px] rounded border border-line bg-surface text-center font-mono text-[22px] uppercase text-ink transition-colors focus:border-ink focus:outline-none aria-[invalid=true]:border-bad"
					onChange={e => {
						const raw = clean(e.currentTarget.value)
						if (raw.length > 1) {
							const next = Array.from({ length }, (_, k) => raw[k] ?? '')
							setChars(next)
							refs.current[Math.min(raw.length, length - 1)]?.focus()
							return
						}
						const next = [...chars]
						next[i] = raw
						setChars(next)
						if (raw) refs.current[i + 1]?.focus()
					}}
					onKeyDown={e => {
						if (e.key === 'Backspace' && !chars[i]) refs.current[i - 1]?.focus()
						if (e.key === 'ArrowLeft') refs.current[i - 1]?.focus()
						if (e.key === 'ArrowRight') refs.current[i + 1]?.focus()
					}}
					onPaste={e => {
						const text = clean(e.clipboardData.getData('text')).slice(0, length)
						if (!text) return
						e.preventDefault()
						setChars(Array.from({ length }, (_, k) => text[k] ?? ''))
						refs.current[Math.min(text.length, length - 1)]?.focus()
					}}
				/>
			))}
		</div>
	)
}

/* ───────────────────────── onboarding chrome ───────────────────────── */

export const ONBOARDING_STEPS = [
	{ key: 'rsvp', label: 'Let’s get started', to: '/onboarding/rsvp' },
	{ key: 'travel', label: 'Your journey', to: '/onboarding/travel' },
	{ key: 'dietary', label: 'Your plate', to: '/onboarding/dietary' },
	{ key: 'thank-you', label: 'Thank you', to: '/onboarding/thank-you' },
] as const
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]['key']

/** Brand + sign-out row (AuthShell `topRight`) for onboarding steps. */
export function SignOutButton() {
	return (
		<Form method="POST" action="/logout">
			<AuthenticityTokenInput />
			<button type="submit" className="text-sm text-stone transition-colors hover:text-ink">
				Sign out
			</button>
		</Form>
	)
}

/** Clickable step strip: done steps link back, current is marked (prototype `.onb-steps`). */
export function OnboardingProgress({ current }: { current: OnboardingStep }) {
	const idx = ONBOARDING_STEPS.findIndex(s => s.key === current)
	return (
		<nav aria-label="Progress" className="mb-10">
			<ol className="flex gap-1.5">
				{ONBOARDING_STEPS.map((s, i) => {
					const done = i < idx
					const isCurrent = i === idx
					const body = (
						<>
							<span className={cn('block h-[3px] rounded-sm transition-colors', i <= idx ? 'bg-ink' : 'bg-line')} />
							<span
								className={cn(
									'mt-3 hidden font-mono text-[10px] uppercase tracking-[.12em] sm:block',
									i <= idx ? 'text-ink' : 'text-stone',
								)}
							>
								{String(i + 1).padStart(2, '0')} · {s.label}
							</span>
						</>
					)
					return (
						<li key={s.key} className="flex-1" aria-current={isCurrent ? 'step' : undefined}>
							{done ? (
								<Link to={s.to} className="block hover:opacity-70">
									{body}
								</Link>
							) : (
								body
							)}
						</li>
					)
				})}
			</ol>
		</nav>
	)
}

/** Bottom action row: back on the left, skip + continue on the right. */
export function StepActions({
	back,
	skip,
	submitLabel = 'Continue →',
	pending,
	left,
}: {
	back?: string
	skip?: string
	submitLabel?: React.ReactNode
	pending?: boolean
	left?: React.ReactNode
}) {
	return (
		<div className="mt-4 flex items-center justify-between gap-4 border-t border-line pt-6">
			{left ?? (back ? (
				<Link
					to={back}
					className="inline-flex h-11 items-center rounded-full border border-line px-[22px] text-sm font-medium transition-colors hover:bg-paper-2"
				>
					← Back
				</Link>
			) : (
				<span />
			))}
			<div className="flex items-center gap-5">
				{skip ? (
					<Link to={skip} className="text-sm text-stone transition-colors hover:text-ink">
						Skip
					</Link>
				) : null}
				<button
					type="submit"
					disabled={pending}
					className={cn(
						'inline-flex h-[52px] items-center gap-2.5 rounded-full border border-ink bg-ink px-7 text-[15px] font-medium text-paper transition-colors hover:bg-ink-2 disabled:opacity-60',
						pending && 'cursor-wait',
					)}
				>
					{pending ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" /> : null}
					{submitLabel}
				</button>
			</div>
		</div>
	)
}

/** A question label above a choice group (prototype `.onb-q`). */
export function Question({ id, children }: { id: string; children: React.ReactNode }) {
	return (
		<p id={id} className="text-[17px] font-medium text-ink">
			{children}
		</p>
	)
}

export function FieldError({ id, errors }: { id?: string; errors?: string[] | string }) {
	const msg = Array.isArray(errors) ? errors[0] : errors
	if (!msg) return null
	return (
		<p id={id} className="text-[13px] text-bad">
			{msg}
		</p>
	)
}

/** Big solid submit button used on the auth forms. */
export function SubmitButton({ pending, children }: { pending?: boolean; children: React.ReactNode }) {
	return (
		<button
			type="submit"
			disabled={pending}
			className="mt-2 inline-flex h-[52px] w-full items-center justify-center gap-2.5 rounded-full border border-ink bg-ink px-7 text-[15px] font-medium text-paper transition-colors hover:bg-ink-2 disabled:opacity-60"
		>
			{pending ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" /> : null}
			{children}
		</button>
	)
}
